import { Column, ColumnType } from '@scale/compass-shared/types';
import { FilterPreset } from '@scale/compass-shared/filter';
import { ContributorInputFilters } from '@scale/shared/types/compass/cards/adapter/contributors';
import { getDateDaysAgo } from 'react-scale/corp/views/DataCompassCardsV2/utils';

/** Input columns for Contributors Adapter filtering. */
const UNSCOPED_COLUMNS: Record<string, Column & { id: keyof ContributorInputFilters }> = {
  workerTeamId: {
    id: 'workerTeamId',
    header: 'Worker Team ID',
    type: ColumnType.TEXT,
  },
  workerTeamName: {
    id: 'workerTeamName',
    header: 'Worker Team Name',
    type: ColumnType.TEXT,
  },
  chosenProjectId: {
    id: 'chosenProjectId',
    header: 'Chosen Project ID',
    type: ColumnType.TEXT,
  },
  directAssignmentProjectIds: {
    id: 'directAssignmentProjectIds',
    header: 'Direct Assignment Project IDs',
    type: ColumnType.ARRAY,
  },
  directAssignmentPrimaryProjectId: {
    id: 'directAssignmentPrimaryProjectId',
    header: 'Direct Assignment Primary Project ID',
    type: ColumnType.TEXT,
  },
  teamAssignmentProjectIds: {
    id: 'teamAssignmentProjectIds',
    header: 'Team Assignment Project IDs',
    type: ColumnType.ARRAY,
  },
  teamAssignmentPrimaryProjectId: {
    id: 'teamAssignmentPrimaryProjectId',
    header: 'Team Assignment Primary Project ID',
    type: ColumnType.TEXT,
  },
  isEmptyQueued: {
    id: 'isEmptyQueued',
    header: 'Is Empty Queued',
    type: ColumnType.BOOLEAN,
  },
  userTagIds: {
    id: 'userTagIds',
    header: 'Tag IDs',
    type: ColumnType.ARRAY,
  },
  countryCode: {
    id: 'countryCode',
    header: 'Country Code',
    type: ColumnType.TEXT,
  },
  workerSource: {
    id: 'workerSource',
    header: 'Worker Source',
    type: ColumnType.TEXT,
  },
  activeMissionIds: {
    id: 'activeMissionIds',
    header: 'Active Mission IDs',
    type: ColumnType.ARRAY,
  },
  currentProjectStep: {
    id: 'currentProjectOnboardingStep',
    header: 'Current Project Step',
    type: ColumnType.TEXT,
  },
  currentProjectId: {
    id: 'currentProjectId',
    header: 'Current Project ID',
    type: ColumnType.TEXT,
  },
  currentProjectReviewLevel: {
    id: 'currentProjectReviewLevel',
    header: 'Current Project Review Level',
    type: ColumnType.TEXT,
  },
  currentProjectPermissionedReviewLevel: {
    id: 'currentProjectPermissionedReviewLevel',
    header: 'Current Project Permissioned Review Level',
    type: ColumnType.NUMBER,
  },
  latestChosenProjectTime: {
    id: 'latestChosenProjectTime',
    header: 'Latest Chosen Project Time',
    type: ColumnType.DATE,
  },
  latestDirectAssignmentTime: {
    id: 'latestDirectAssignmentTime',
    header: 'Latest Direct Assignment Time',
    type: ColumnType.DATE,
  },
  lastActiveAt: {
    id: 'lastActiveAt',
    header: 'Last Active',
    type: ColumnType.DATE,
  },
  lastOnlineAt: {
    id: 'lastOnlineAt',
    header: 'Last Online',
    type: ColumnType.DATE,
  },
  experiencePlatform: {
    id: 'experiencePlatform',
    header: 'Platform Experience',
    type: ColumnType.TEXT,
  },
  experienceCustomerId: {
    id: 'experienceCustomerId',
    header: 'Customer Experience',
    type: ColumnType.TEXT,
  },
  experienceProjectAttribute: {
    id: 'experienceProjectAttribute',
    header: 'Project Attribute Experience',
    type: ColumnType.TEXT,
  },
  experienceMinTasks: {
    id: 'experienceMinTasks',
    header: 'Min Tasks Experience',
    type: ColumnType.NUMBER,
  },
  marketplaceOnboardingNumProjects: {
    id: 'marketplaceOnboardingNumProjects',
    header: 'Marketplace Onboarding # Projects',
    type: ColumnType.NUMBER,
  },
  marketplaceOnboardingFailRate: {
    id: 'marketplaceOnboardingFailRate',
    header: 'Marketplace Onboarding Fail Rate',
    type: ColumnType.NUMBER,
  },
};

export const CB_SEARCH_REVAMP_UNSCOPED_COLUMNS: Record<
  string,
  Column & { id: keyof ContributorInputFilters }
> = {
  cohortIds: {
    id: 'cohortIds',
    header: 'Cohorts',
    type: ColumnType.ARRAY,
  },
  queuedProjectId: {
    id: 'queuedProjectId',
    header: 'Queued Project ID',
    type: ColumnType.TEXT,
  },
  latestQueueRunEvent: {
    id: 'latestQueueRunEvent',
    header: 'Last Queue Run Event',
    type: ColumnType.TEXT,
  },
  hoursWorked: {
    id: 'hoursWorked',
    header: 'Hours Worked',
    type: ColumnType.NUMBER,
  },
  linkedInNumConnections: {
    id: 'linkedInNumConnections',
    header: 'LinkedIn Num Connections',
    type: ColumnType.NUMBER,
  },
  squadLeadEmail: {
    id: 'squadLeadEmail',
    header: 'Squad Lead Email',
    type: ColumnType.TEXT,
  },
  currentProjectOnboardingFlowId: {
    id: 'currentProjectOnboardingFlowId',
    header: 'Current Project Onboarding Flow ID',
    type: ColumnType.TEXT,
  },
  currentOCPFlowId: {
    id: 'currentOCPFlowId',
    header: 'Current OCP Flow ID',
    type: ColumnType.TEXT,
  },
  currentOCPStep: {
    id: 'currentOCPStep',
    header: 'Current OCP Step',
    type: ColumnType.TEXT,
  },
  userQMS: {
    id: 'userQMS',
    header: 'User QMS',
    type: ColumnType.NUMBER,
  },
  numReviewsCompleted: {
    id: 'numReviewsCompleted',
    header: 'Reviews Completed',
    type: ColumnType.NUMBER,
  },
  numLevel8AndAboveReviewsCompleted: {
    id: 'numLevel8AndAboveReviewsCompleted',
    header: 'L8+ Reviews Completed',
    type: ColumnType.NUMBER,
  },
  disabledPercentage: {
    id: 'disabledPercentage',
    header: 'Disabled Percentage',
    type: ColumnType.NUMBER,
  },
  numProjectsWorkedOn: {
    id: 'numProjectsWorkedOn',
    header: 'Num Projects Worked On',
    type: ColumnType.NUMBER,
  },
  numWebinarsAttended: {
    id: 'numWebinarsAttended',
    header: 'Number of Webinars Attended',
    type: ColumnType.NUMBER,
  },
  userTagNames: {
    id: 'userTagNames',
    header: 'Tag Names',
    type: ColumnType.ARRAY,
  },
  inFraudHolding: {
    id: 'inFraudHolding',
    header: 'In Fraud Holding',
    type: ColumnType.BOOLEAN,
  },
  totalHubstaffHoursWorked: {
    id: 'totalHubstaffHoursWorked',
    header: 'Total Hubstaff Hours Worked',
    type: ColumnType.NUMBER,
  },
  lastActiveOnHubstaff: {
    id: 'lastActiveOnHubstaff',
    header: 'Last Active on Hubstaff',
    type: ColumnType.DATE,
  },
};

export const LEGACY_UNSCOPED_COLUMNS: Record<
  string,
  Column & { id: keyof ContributorInputFilters }
> = {
  workerSkillIds: {
    id: 'workerSkillIds',
    header: 'Worker Skill IDs',
    type: ColumnType.ARRAY,
  },
  workerSkillName: {
    id: 'workerSkillName',
    header: 'Worker Skill Name',
    type: ColumnType.TEXT,
  },
  workerSkillTier: {
    id: 'workerSkillTier',
    header: 'Worker Skill Tier',
    type: ColumnType.TEXT,
  },
};

export const SCOPING_COLUMNS: Record<string, Column & { id: keyof ContributorInputFilters }> = {
  project: {
    id: 'project',
    header: 'Project ID',
    type: ColumnType.TEXT,
    scopingColumn: true,
  },
  workerSkill: {
    id: 'workerSkill',
    header: 'Worker Skill',
    type: ColumnType.TEXT,
    scopingColumn: true,
  },
};

const PROJECT_SCOPED_COLUMNS: Record<string, Column & { id: keyof ContributorInputFilters }> = {
  projectHighestReviewLevel: {
    id: 'project.highestReviewLevel',
    header: 'Highest Review Level',
    type: ColumnType.NUMBER,
  },
  projectSpecializations: {
    id: 'project.specializations',
    header: 'Specializations',
    type: ColumnType.ARRAY,
  },
  projectPreferReviewLevelsAtMost: {
    id: 'project.preferReviewLevelsAtMost',
    header: 'Prefer Review Levels At Most',
    type: ColumnType.NUMBER,
  },
  projectDisabled: {
    id: 'project.disabled',
    header: 'Disabled',
    type: ColumnType.BOOLEAN,
  },
  projectDisableReason: {
    id: 'project.disableReason',
    header: 'Disable Reason',
    type: ColumnType.TEXT,
  },
  projectThrottleStatus: {
    id: 'project.throttleStatus',
    header: 'Throttled',
    type: ColumnType.BOOLEAN,
  },
  projectNumTasksCompleted: {
    id: 'project.numTasksCompleted',
    header: 'Num Tasks Completed',
    type: ColumnType.NUMBER,
  },
  projectAverageMinutesTasking: {
    id: 'project.averageMinutesTasking',
    header: 'Average Minutes Tasking',
    type: ColumnType.NUMBER,
  },
  projectLastTaskSubmittedAt: {
    id: 'project.lastTaskSubmittedAt',
    header: 'Last Task Submitted At',
    type: ColumnType.DATE,
  },
  projectProjectRanking: {
    id: 'project.projectRanking',
    header: 'Assignment Ranking',
    type: ColumnType.NUMBER,
  },
  projectIsCurrentlyAssigned: {
    id: 'project.isCurrentlyAssigned',
    header: 'Is Currently Assigned',
    type: ColumnType.BOOLEAN,
  },
  projectIsMostRecentAssignment: {
    id: 'project.isMostRecentAssignment',
    header: 'Is Most Recent Assignment',
    type: ColumnType.BOOLEAN,
  },
  projectQMS: {
    id: 'project.qmsScore',
    header: 'Project QMS',
    type: ColumnType.NUMBER,
  },
  projectQMSCount: {
    id: 'project.qmsCount',
    header: '# QMS Scores',
    type: ColumnType.NUMBER,
  },
  projectAuditScore: {
    id: 'project.auditScore',
    header: 'Audit Score',
    type: ColumnType.NUMBER,
  },
  projectAuditCount: {
    id: 'project.auditCount',
    header: '# Audits',
    type: ColumnType.NUMBER,
  },
  projectArchieQCScore: {
    id: 'project.archieQCScore',
    header: 'ArchieQC Score',
    type: ColumnType.NUMBER,
  },
  projectArchieQCCount: {
    id: 'project.archieQCCount',
    header: '# ArchieQC Scores',
    type: ColumnType.NUMBER,
  },
  projectBMScore: {
    id: 'project.bmScore',
    header: 'Benchmark Average Score',
    type: ColumnType.NUMBER,
  },
  projectBMCount: {
    id: 'project.bmCount',
    header: '# Benchmarks',
    type: ColumnType.NUMBER,
  },
  projectQCPDR: {
    id: 'project.qcPDR',
    header: 'QC PDR',
    type: ColumnType.NUMBER,
  },
  projectQCScore: {
    id: 'project.qcScore',
    header: 'QC Score',
    type: ColumnType.NUMBER,
  },
  projectQCCount: {
    id: 'project.qcCount',
    header: '# QC Scores',
    type: ColumnType.NUMBER,
  },
  projectHubstaffHoursWorked: {
    id: 'project.hubstaffHoursWorked',
    header: 'Total Hubstaff Hours Worked',
    type: ColumnType.NUMBER,
  },
  projectLastActiveOnHubstaff: {
    id: 'project.lastActiveOnHubstaff',
    header: 'Last Active on Hubstaff',
    type: ColumnType.DATE,
  },
};

const WORKER_SKILL_SCOPED_COLUMNS: Record<string, Column & { id: keyof ContributorInputFilters }> =
  {
    workerSkillTier: {
      id: 'workerSkill.tier',
      header: 'Worker Skill Tier',
      type: ColumnType.NUMBER,
    },
    workerSkillStatus: {
      id: 'workerSkill.status',
      header: 'Worker Skill Status',
      type: ColumnType.TEXT,
    },
    hasWorkerSkill: {
      id: 'workerSkill.hasWorkerSkill',
      header: 'Has Worker Skill',
      type: ColumnType.BOOLEAN,
    },
  };

export const INPUT_COLUMNS: Record<string, Column & { id: keyof ContributorInputFilters }> = {
  ...UNSCOPED_COLUMNS,
  ...LEGACY_UNSCOPED_COLUMNS,
};

export const CB_SEARCH_REVAMP_INPUT_COLUMNS: Record<
  string,
  Column & { id: keyof ContributorInputFilters }
> = {
  ...UNSCOPED_COLUMNS,
  ...CB_SEARCH_REVAMP_UNSCOPED_COLUMNS,
  ...SCOPING_COLUMNS,
  ...PROJECT_SCOPED_COLUMNS,
  ...WORKER_SKILL_SCOPED_COLUMNS,
};

export const FILTER_PRESET_MARKETPLACE: FilterPreset = {
  name: 'Marketplace',
  filter: {
    operator: 'and',
    filtersOrFilterGroups: [
      {
        column: INPUT_COLUMNS.workerTeamId,
        condition: 'is',
        // Outlier/marketplace WT
        term: '6669ddd4f3127a2e7e71b6cc',
      },
    ],
  },
};

export const FILTER_PRESET_PRIMARY_PROJECTS: FilterPreset = {
  name: 'Primary Projects',
  filter: {
    operator: 'or',
    filtersOrFilterGroups: [
      {
        column: INPUT_COLUMNS.chosenProjectId,
        condition: 'is',
        term: '',
      },
      {
        column: INPUT_COLUMNS.directAssignmentPrimaryProjectId,
        condition: 'is',
        term: '',
      },
      {
        column: INPUT_COLUMNS.teamAssignmentPrimaryProjectId,
        condition: 'is',
        term: '',
      },
    ],
  },
};

export const FILTER_PRESET_ELIGIBLE_DIRECT_ASSIGN_CONTRIBUTORS: FilterPreset = {
  name: 'Eligible Contributors for Direct Assignment',
  filter: {
    operator: 'and',
    filtersOrFilterGroups: [
      {
        column: INPUT_COLUMNS.directAssignmentPrimaryProjectId,
        condition: 'is_empty',
        term: '',
      },
      {
        column: INPUT_COLUMNS.latestChosenProjectTime,
        condition: 'greater_than',
        term: getDateDaysAgo(2),
      },
      {
        column: INPUT_COLUMNS.activeMissionIds,
        condition: 'is_empty',
        term: '',
      },
      {
        operator: 'or',
        filtersOrFilterGroups: [
          {
            column: INPUT_COLUMNS.currentProjectStep,
            condition: 'is_empty',
            term: '',
          },
          {
            column: INPUT_COLUMNS.currentProjectStep,
            condition: 'is',
            term: 'Allocated',
          },
          {
            column: INPUT_COLUMNS.currentProjectStep,
            condition: 'is',
            term: 'Failed',
          },
          {
            column: INPUT_COLUMNS.currentProjectStep,
            condition: 'is',
            term: 'Removed',
          },
          {
            column: INPUT_COLUMNS.currentProjectStep,
            condition: 'is',
            term: 'Success',
          },
        ],
      },
    ],
  },
};
